import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/services/api';

interface ConsentRecord {
  id: string;
  consent_type: string;
  granted: boolean;
  granted_at?: string;
  revoked_at?: string;
  version: string;
}

interface DataExportRequest {
  id: string;
  status: string;
  requested_at: string;
  completed_at?: string;
  download_url?: string;
  download_expires_at?: string;
}

export default function ProfilePage() {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState<'profile' | 'privacy' | 'danger'>('profile');
  const [consents, setConsents] = useState<ConsentRecord[]>([]);
  const [exportRequest, setExportRequest] = useState<DataExportRequest | null>(null);
  const [deletionPending, setDeletionPending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadPrivacySettings();
  }, []);

  async function loadPrivacySettings() {
    try {
      setLoading(true);
      const response = await api.get('/profiles/me/consents');
      setConsents(response.data.consents || []);
    } catch (e) {
      // Consents might not exist yet
      setConsents([]);
    } finally {
      setLoading(false);
    }
  }

  async function updateConsent(type: string, granted: boolean) {
    try {
      await api.put(`/profiles/me/consents/${type}`, {
        granted,
        version: '1.0',
      });
      setConsents(consents.map((c) =>
        c.consent_type === type ? { ...c, granted } : c
      ));
      setSuccess('Preference updated');
      setTimeout(() => setSuccess(null), 3000);
    } catch (e) {
      setError('Failed to update preference');
    }
  }

  async function requestDataExport() {
    if (!confirm('Request a copy of all your data? This may take some time to prepare.')) return;

    try {
      const response = await api.post('/profiles/me/export');
      setExportRequest(response.data);
      setSuccess('Data export requested. You will be notified when it is ready.');
      setTimeout(() => setSuccess(null), 5000);
    } catch (e: unknown) {
      setError((e as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Failed to request data export');
    }
  }

  async function requestAccountDeletion() {
    const reason = prompt('Why are you leaving? (optional)');
    if (reason === null) return; // Cancelled

    if (!confirm('Are you sure you want to delete your account? You will have 14 days to cancel this request.')) return;

    try {
      await api.delete('/profiles/me', { data: { reason } });
      setDeletionPending(true);
      setSuccess('Account deletion scheduled. You have 14 days to cancel this request.');
    } catch (e: unknown) {
      setError((e as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Failed to request account deletion');
    }
  }

  async function cancelDeletion() {
    try {
      await api.post('/profiles/me/deletion/cancel');
      setDeletionPending(false);
      setSuccess('Account deletion cancelled');
      setTimeout(() => setSuccess(null), 3000);
    } catch (e: unknown) {
      setError((e as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Failed to cancel deletion');
    }
  }

  const consentTypes = [
    { type: 'terms_of_service', label: 'Terms of Service', description: 'Required to use the platform', required: true },
    { type: 'privacy_policy', label: 'Privacy Policy', description: 'Required to use the platform', required: true },
    { type: 'marketing_emails', label: 'Marketing Emails', description: 'Receive news, updates, and promotional content' },
    { type: 'analytics_tracking', label: 'Analytics', description: 'Help us improve by allowing anonymous usage analytics' },
    { type: 'third_party_sharing', label: 'Third-Party Sharing', description: 'Allow sharing data with trusted partners' },
  ];

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-8">Account Settings</h1>

      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">Dismiss</button>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-50 text-green-700 rounded-lg">{success}</div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-8">
          <TabButton active={activeTab === 'profile'} onClick={() => setActiveTab('profile')}>
            Profile
          </TabButton>
          <TabButton active={activeTab === 'privacy'} onClick={() => setActiveTab('privacy')}>
            Privacy
          </TabButton>
          <TabButton active={activeTab === 'danger'} onClick={() => setActiveTab('danger')}>
            Account
          </TabButton>
        </nav>
      </div>

      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Profile Information</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <p className="mt-1 text-gray-900">{user?.email}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Account Type</label>
              <p className="mt-1 text-gray-900 capitalize">{user?.user_type}</p>
            </div>
            <p className="text-sm text-gray-500">
              Full profile editing will be available in your {user?.user_type === 'expert' ? 'Expert Dashboard' : 'Company Dashboard'}.
            </p>
          </div>
        </div>
      )}

      {/* Privacy Tab */}
      {activeTab === 'privacy' && (
        <div className="space-y-6">
          {/* Consent Management */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">Privacy Preferences</h2>
            <p className="text-sm text-gray-500 mb-4">
              Manage how we use your data. Required consents cannot be changed while your account is active.
            </p>

            {loading ? (
              <div className="animate-pulse space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-12 bg-gray-200 rounded"></div>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {consentTypes.map((consent) => {
                  const record = consents.find((c) => c.consent_type === consent.type);
                  const isGranted = record?.granted ?? consent.required;

                  return (
                    <div key={consent.type} className="flex items-center justify-between py-3 border-b last:border-0">
                      <div>
                        <p className="font-medium">{consent.label}</p>
                        <p className="text-sm text-gray-500">{consent.description}</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isGranted}
                          onChange={(e) => updateConsent(consent.type, e.target.checked)}
                          disabled={consent.required}
                          className="sr-only peer"
                        />
                        <div className={`w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer ${
                          isGranted ? 'peer-checked:bg-blue-600' : ''
                        } peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all ${
                          consent.required ? 'opacity-50 cursor-not-allowed' : ''
                        }`}></div>
                      </label>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Data Export */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">Your Data</h2>
            <p className="text-sm text-gray-500 mb-4">
              Under GDPR Article 15, you have the right to receive a copy of all your personal data.
            </p>

            {exportRequest && exportRequest.status === 'completed' && exportRequest.download_url ? (
              <div className="p-4 bg-green-50 rounded-lg mb-4">
                <p className="text-green-800">Your data export is ready!</p>
                <a
                  href={exportRequest.download_url}
                  className="text-green-600 underline text-sm"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download your data
                </a>
                <p className="text-xs text-green-600 mt-1">
                  Link expires: {new Date(exportRequest.download_expires_at!).toLocaleString()}
                </p>
              </div>
            ) : exportRequest && ['pending', 'processing'].includes(exportRequest.status) ? (
              <div className="p-4 bg-yellow-50 rounded-lg mb-4">
                <p className="text-yellow-800">Data export in progress...</p>
                <p className="text-sm text-yellow-600">We'll notify you when it's ready.</p>
              </div>
            ) : null}

            <button
              onClick={requestDataExport}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Request Data Export
            </button>
          </div>
        </div>
      )}

      {/* Account/Danger Zone Tab */}
      {activeTab === 'danger' && (
        <div className="space-y-6">
          {/* Session Management */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">Session</h2>
            <button
              onClick={logout}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Log Out
            </button>
          </div>

          {/* Delete Account */}
          <div className="bg-white rounded-lg shadow p-6 border-2 border-red-200">
            <h2 className="text-lg font-semibold text-red-600 mb-4">Danger Zone</h2>

            {deletionPending ? (
              <div className="p-4 bg-red-50 rounded-lg mb-4">
                <p className="text-red-800 font-medium">Account deletion is scheduled</p>
                <p className="text-sm text-red-600 mb-4">
                  Your account will be permanently deleted in 14 days.
                  All personal data will be anonymized, but financial records will be retained for 7 years as required by Irish law.
                </p>
                <button
                  onClick={cancelDeletion}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Cancel Deletion
                </button>
              </div>
            ) : (
              <>
                <p className="text-sm text-gray-500 mb-4">
                  Permanently delete your account and all associated data. Under GDPR Article 17, you have the right to erasure.
                  You will have 14 days to change your mind.
                </p>
                <button
                  onClick={requestAccountDeletion}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Delete My Account
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

function TabButton({ active, onClick, children }: TabButtonProps) {
  return (
    <button
      onClick={onClick}
      className={`pb-4 text-sm font-medium border-b-2 transition-colors ${
        active
          ? 'border-blue-600 text-blue-600'
          : 'border-transparent text-gray-500 hover:text-gray-700'
      }`}
    >
      {children}
    </button>
  );
}
